import { ensureSwiperIfPresent } from "./vanilla/swiper-loader"
import { initAiLabelSticky } from "./vanilla/ai-label-sticky"
import { initCornerPopup } from "./vanilla/corner-popup"
import "./scss/global.scss"

import { createApp } from "vue"
import { gsap, ScrollTrigger } from "@/composeables/gsap"
import debounce from "lodash.debounce"
// three.js/troisjs entfernt: der einzige Nutzer war <webgl-scene> (sectionWebgl),
// und kein Content verwendet diesen Block (0 Blöcke, 0 GLTF-Assets). troisjs' createApp
// zog three (~500 KB) in JEDES Haupt-Bundle. Standard-Vue-createApp spart das komplett ein.
// gsap/ScrollTrigger bleiben global verfügbar (evtl. GTM-Custom-HTML nutzt sie).
window.gsap = gsap;
window.ScrollTrigger = ScrollTrigger;

import { createPinia } from 'pinia'
// import { useBreakpoints } from "@vueuse/core";
// import { OnClickOutside } from "@vueuse/components";
// import { ExpandTransition, FadeTransition } from "@bam/transitions";

// Gezielte Named-Imports statt `import * as ANIMATIONS`: der Namespace-Import
// hielt den KOMPLETTEN animations-Barrel im Haupt-Bundle — auch die nie
// aufgerufenen Module (sectionStage, sectionText, sectionForm, floatingActionBar,
// sectionCards, sectionHeroStage, zoomMedia). sectionStage.js zog dadurch
// gsap/Flip tot ins Bundle. Mit Named-Imports tree-shaked Vite die ungenutzten
// Module + Flip heraus. Reihenfolge = Aufrufreihenfolge in initSectionAnimations.
import {
    initSectionPinnedMedia,
    initSectionPractitioners,
    initSectionPractitionersStage,
    initSectionTextOnImage,
    initSectionProductTeaser,
    initSectionTextMedia,
    initSectionMedia,
    initSectionMap,
    initSectionCardSlider,
    initSectionKpiCounter,
    initSectionScrollStage,
    initSectionFacts,
    initSectionLogoWall,
    initSectionTrustAnchors,
    initSectionPublicMedia,
    initSectionLocations,
    initSectionCtaShine,
} from './animations'
import { init as fancyboxInit } from "./vanilla/fanybox"
import { mountFancyboxOverlay } from "./vanilla/fancybox-vue"
import autoRegisterComponents from "./plugins/auto-register-components"

let { height: bodyHeight, width: bodyWidth } = document.body.getBoundingClientRect().height;
const handleResize = debounce((entries, observer) => {
    const [ entry, ..._ ] = entries

    const newHeight = Math.round(entry.contentRect.height)
    const newWidth = Math.round(entry.contentRect.width)

    if (newHeight != bodyHeight || bodyWidth != newWidth) {
        bodyHeight = newHeight
        bodyWidth = newWidth
        setVh()
        ScrollTrigger.refresh();
    }
}, 30)
const resizeObserver = new ResizeObserver(handleResize);
resizeObserver.observe(document.body)

function setVh() {
    document.documentElement.style.setProperty('--vh', `${window.innerHeight * 0.01}px`)
}
setVh()
const handleWindowResize = debounce(() => { setVh() }, 50)
window.addEventListener('scroll', setVh, { passive: true })
// window.addEventListener('resize', (event) => { console.log(window.innerHeight); handleWindowResize(event)})
window.addEventListener('orientationchange', handleWindowResize)


Promise
    .all(
        Array.from(document.images)
            .filter(img => !img.complete)
            .map(img => new Promise(resolve => { img.onload = img.onerror = resolve; }))
    )
    .then(() => {
        window.setTimeout(() => { ScrollTrigger.refresh(); }, 0)
    });

// window.addEventListener("hashchange", () => {
//     setVh()
//     ScrollTrigger.refresh()
// })

// document.addEventListener("click", (event) => {
//     const { target } = event
//     const link = target.matches('a[href*="#"]')
//         ? target
//         : target.closest('a[href*="#"]')

//     if (!link) return;

//     const url = new URL(link.getAttribute("href"));
//     if (!(
//         url.protocol === location.protocol
//         && url.origin === location.origin
//         && url.pathname === location.pathname
//     )) {
//         return
//     }

//     event.preventDefault();

//     if (url.hash === "") {
//         history.pushState(null, null, url)
//         gsap.to(window, { scrollTo: { y: 0, autoKill: false } });
//         return
//     }

//     const href = url.hash.split('#').slice(1).join('#')
//     const hrefTarget = document.getElementById(href)
//     if (!hrefTarget) {
//         return
//     }

//     history.pushState(null, null, url)
//     gsap.to(window, { scrollTo: { y: hrefTarget, offsetY: 50, autoKill: false } });
// })

const app = createApp({
    data: () => {
        return {
            // globalSwiperModules: [Pagination, Parallax],
        };
    },
    update() {
        // console.log("vue update")
        ScrollTrigger.refresh()
    },
    mounted() {
        ensureSwiperIfPresent();

        // Die Section-Animationen (Canvas-Setup der Scroll-Bühne, ScrollTrigger-Pins,
        // Swiper-Slider …) laufen synchron und blockieren den Main-Thread. Liefen sie
        // direkt im mounted(), verzögern sie den ersten Paint des Poster-<img> (LCP)
        // um ~1,3 s auf Mobile. Doppeltes rAF: der Browser committet garantiert erst
        // den Poster-Frame, DANN bauen wir die Animationen auf. Die Reveals sind
        // scrollgetriggert, ein paar ms später zu starten ist unsichtbar.
        const initSectionAnimations = () => {
            initSectionPinnedMedia();
            initSectionPractitioners();
            initSectionTextOnImage();
            initSectionProductTeaser();
            initSectionTextMedia();
            initSectionMedia();
            initSectionMap();
            initSectionCardSlider();
            initSectionKpiCounter();
            initSectionFacts();
            initSectionLogoWall();
            initSectionTrustAnchors();
            initSectionPublicMedia();
            initSectionLocations();
            initSectionCtaShine();
            // EU AI Act (Art. 50): ALLE KI-Label per JS positionieren (10px unten
            // rechts, sticky bis zur Bildoberkante). Deterministisch cross-browser –
            // CSS-sticky ist zwischen Firefox/Chromium nicht konsistent.
            initAiLabelSticky();
            // Corner-Popup freigeben (CSS startet unsichtbar) bzw. zugeklappt
            // lassen, wenn es in dieser Session schon geschlossen wurde. Liegt
            // hinter dem ersten Paint, damit es den LCP nicht verzögert.
            initCornerPopup();
            ScrollTrigger.refresh()

        }
        requestAnimationFrame(() => requestAnimationFrame(initSectionAnimations))

        // Scroll-Bühne + Ärzte-Bühne (PractitionersStage) separat und noch später
        // als der Rest: beide bauen je (mind.) einen ScrollTrigger.create mit
        // pin:true auf, was laut Lighthouse-Trace zusammen ~450-500ms erzwungene
        // Reflows auf Mobile (4x CPU) kostete – mit Abstand der teuerste Anteil
        // am LCP-Render-Delay. Beide Sections liegen deutlich unterhalb des
        // ersten Viewports (Ärzte-Bühne z. B. bei ~3900px), ein paar hundert ms
        // späterer Aufbau ist unsichtbar.
        // Zwei Anläufe verworfen: (1) requestIdleCallback INNERHALB des
        // doppelten-rAF-Batches half nichts – der Browser wertet "idle" aus,
        // bevor er den Poster-Frame tatsächlich gemalt hat, kein neuer Task/
        // Paint-Zyklus dazwischen. (2) window.load als Gate half auch kaum –
        // auf Slow 4G lädt window.load erst, wenn ALLE Ressourcen (u. a. der
        // Fanybox-Chunk, MegaNav-CSS) durch sind, das zog den Start selbst
        // wieder in die Nähe des LCP-Zeitpunkts. Jetzt: ein eigener, später
        // Timer (~200ms) NACH dem doppelten rAF – lang genug für einen
        // zusätzlichen echten Paint-Zyklus, aber unabhängig von Netzwerk-
        // Ressourcen. ScrollTrigger.refresh() danach ist nötig, weil pin:true
        // Scroll-Spacing einfügt, von dem andere Trigger abhängen.
        // `done`-Guard ist PFLICHT: scrollt der User innerhalb der 200ms, läuft run()
        // aus dem Scroll-Handler UND danach nochmal aus dem Timeout — die Section
        // würde doppelt initialisiert. Lokal reproduziert (Scroll-Event direkt bei
        // `vue:mounted`): ohne Guard 2 Pins statt 1 auf der Bühne und 39 statt 33
        // ScrollTrigger; die Bühne bleibt komplett schwarz, weil zwei konkurrierende
        // Timelines auf denselben Elementen arbeiten — das Canvas startet nie
        // (`is-visible` kommt nicht) und die Overlays bleiben auf autoAlpha 0.
        // Auf Mobile trat das real auf, weil dort im 200ms-Fenster gescrollt wird
        // (Daumen-Wisch, Adressleisten-Kollaps, Scroll-Restoration beim Reload).
        // Gleiches Muster wie das `pumped`-Flag in sectionScrollStage.js.
        const deferHeavyInit = (fn) => {
            let done = false;
            const run = () => {
                if (done) return;
                done = true;
                fn();
                ScrollTrigger.refresh();
            };
            window.addEventListener("scroll", run, { once: true, passive: true });
            setTimeout(() => {
                window.removeEventListener("scroll", run);
                run();
            }, 200);
        };
        deferHeavyInit(initSectionScrollStage);
        deferHeavyInit(initSectionPractitionersStage);

        window.__VUE_APP_MOUNTED__ = true
        document.dispatchEvent(new Event('vue:mounted'))
    },
});

const pinia = createPinia()
app.use(pinia)

// app.config.compilerOptions.isCustomElement = (tag) => {
//     return tag.startsWith('swiper')
// }
// const bps = JSON.parse(
//     getComputedStyle(document.body).getPropertyValue("--bam-breakpoints")
// );
// const breakpoints = useBreakpoints(bps);
// app.config.globalProperties.$breakpoints = breakpoints;

app.use(autoRegisterComponents);

// app.component("expand-transition", ExpandTransition);
// app.component("fade-transition", FadeTransition);
// app.component("on-click-outside", OnClickOutside);

app.mount("#app");

// Accept HMR as per: https://vitejs.dev/guide/api-hmr.html
// if (import.meta.hot) {
//     import.meta.hot.accept();
// }

fancyboxInit({ onReveal: mountFancyboxOverlay });
